import type { UseQueryResult, UseSuspenseQueryResult } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import type { SearchSubscriptionResponse } from '@//app/api/protected/subscriptions/search/[id]';
import type { ElasticListVehicle, ElasticVehiclesResponse } from '@//app/api/search/vehicles';
import { Alert } from '@//components/Alert';
import { Grid } from '@//components/Grid';
import ProductCardContainer from '@//containers/ProductCardContainer';
import type { ElasticSearchResponse } from '@//ElasticSearch';
import { SearchResultSkeleton } from './SearchResultSkeleton';

interface SearchResultProps {
  data?: ElasticSearchResponse<ElasticListVehicle>;
  query:
    | UseQueryResult<ElasticVehiclesResponse, Error>
    | UseSuspenseQueryResult<ElasticVehiclesResponse, Error>;
  searchSubscriptionQuery?: UseQueryResult<SearchSubscriptionResponse, Error>;
  itemPage: string;
  currency: string;
  onLinkClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  isPolling?: boolean;
}

const SearchResult = ({
  data,
  query,
  searchSubscriptionQuery,
  itemPage,
  currency,
  onLinkClick,
  isPolling,
}: SearchResultProps) => {
  const t = useTranslations('pages.search.results');

  if (query.isError) {
    return (
      <Alert id="alert-no-reseults" variant="critical" heading={t('error.heading')}>
        {t('error.message', {
          message: query.error?.message || t('error.unknown'),
        })}
      </Alert>
    );
  }

  if (!query.isFetching && !data?.documentList.documents.length) {
    return (
      <Alert id="alert-no-reseults" heading={t('noResults.heading')}>
        {t('noResults.message')}
      </Alert>
    );
  }

  // Skeleton policy keys off isFetching, which now cleanly distinguishes the
  // two interaction modes after the pagination rework:
  //
  //   - Pagination uses real Next.js navigation + SSR initialData. React Query
  //     never starts a client fetch (isFetching=false) — new server data is
  //     just there on the next render. No skeleton, instant swap.
  //
  //   - Filters use pushState + a client refetch (isFetching=true). The user
  //     signalled "show me something DIFFERENT"; leaving stale results under
  //     a placeholder would imply the filter didn't take. Skeleton acknowledges
  //     the new intent until the new data arrives.
  if (!isPolling && (query.isFetching || searchSubscriptionQuery?.isFetching)) {
    return (
      <Grid
        columns={3}
        role="region"
        aria-label={t('ariaLabel')}
        aria-busy="true"
        id="search-results"
        tabIndex={-1}
      >
        <SearchResultSkeleton />
      </Grid>
    );
  }

  return (
    <Grid columns={3} role="region" aria-label={t('ariaLabel')} id="search-results" tabIndex={-1}>
      {data?.documentList.documents.map((item, index) => (
        <ProductCardContainer
          key={item._id}
          index={index}
          parameters={query.data?.query.parameters}
          testId={`product-card-${index + 1}`}
          item={item}
          itemPage={itemPage}
          currency={currency}
          onLinkClick={onLinkClick}
        />
      ))}
    </Grid>
  );
};

export default SearchResult;
